import React, { FC, CSSProperties, useEffect, useState } from 'react';
import cn from 'classnames';
import { v4 as uuid4 } from 'uuid';
import { Carousel } from 'react-responsive-carousel';

import { View360Icon } from '@assets/svg/view360icon';
import { useDeviceDetect } from '@hooks/usedevicedetect';
import { MainButtons } from '../navigationbar';
import Loader from '@components/loader/loader';
import Typography from '@components/typography';
import { useAppSelector } from '@hooks/statehooks';

import { NextChevron } from '@assets/svg/nextchevron';
import { PreviousChevron } from '@assets/svg/previouschevron';
import { Vehicle } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicle';

import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { VehicleImageInfo } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicleimageinfo';

interface Props {
  className?: string;
  text?: string;
  onMainSelected: (mainSelected: string) => void;
  imageList?: VehicleImageInfo[];
  vehicle: Vehicle;
}

const arrowStyles: CSSProperties = {
  position: 'absolute',
  zIndex: 2,
  top: 'calc(50% - 40px)',
  width: 70,
  height: 70,
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
};

type DivProps = React.HTMLAttributes<HTMLDivElement>;
const imageThumbSize = 140;
export interface GalleryImageProps extends DivProps {
  key: string;
  url: string;
  altText?: string;
}

const galleryImageExplanation =
  'The images on here are just to give you an idea. Not your actual car.';

const GalleryImage: React.FC<GalleryImageProps> = ({ key, url, altText }) => {
  return <img key={key} src={url} alt={altText} />;
};

const Gallery: FC<Props> = ({
  className,
  onMainSelected,
  imageList = [],
  vehicle,
}: Props) => {
  const { currentWidth, isMobile } = useDeviceDetect();
  const [lastItem, setLastItem] = useState<boolean>(false);
  const [firstItem, setFirstItem] = useState<boolean>(false);

  const { make, model } = vehicle;

  // Going by Izmos formatting, these should always be in this orientation
  const thumbAccessibleNames = [
    `Rear side of ${make} ${model}`,
    `Front of ${make} ${model}`,
    `Front side of ${make} ${model}`,
    `Side of ${make} ${model}`,
    `Rear of ${make} ${model} with boot open`,
  ];

  const vehicleImages = useAppSelector((state) => state.vehicleImageGallery);

  useEffect(() => {
    if (!vehicleImages.error) {
      let listLength =
        vehicleImages.imageList.length > 0
          ? vehicleImages.imageList.length
          : imageList
          ? imageList.length
          : 0;
      let thumbs = document.getElementsByClassName(
        'thumbs'
      )[0] as HTMLUListElement;
      let halfTransformSize = (listLength * imageThumbSize) / 3;
      if (thumbs && lastItem) {
        const computed = window
          .getComputedStyle(thumbs)
          .getPropertyValue('transform');

        if (computed) {
          let transformValuetX = new DOMMatrix(computed);
          if (transformValuetX.e === 0) {
            thumbs.style.setProperty(
              'transform',
              `translate3d(${-imageThumbSize * 2}px, 0px, 0px)`
            );
          } else if (
            halfTransformSize &&
            -transformValuetX.e < halfTransformSize
          ) {
            let transformValue =
              transformValuetX && -transformValuetX.e >= imageThumbSize
                ? transformValuetX.e - imageThumbSize
                : -imageThumbSize;
            thumbs.style.setProperty(
              'transform',
              `translate3d(${transformValue}px, 0px, 0px)`
            );
          }
        }
      } else if (thumbs && firstItem) {
        thumbs.style.setProperty('transform', 'translate3d(0px, 0px, 0px)');
      }
    }
  }, [firstItem, lastItem, vehicleImages.loading, imageList]);

  useEffect(() => {
    // We do this to override the attributes on the div's the carousel component
    const thumbsContainer = document.querySelector('.thumbs');
    if (thumbsContainer) {
      const thumbDivs = thumbsContainer.querySelectorAll('li');
      thumbDivs.forEach((listItem, index) => {
        if (index < thumbAccessibleNames.length) {
          listItem.removeAttribute('aria-label');
          listItem.removeAttribute('role');
          listItem.removeAttribute('tabindex');
          listItem.setAttribute('title', `${thumbAccessibleNames[index]}`);
        }
      });
      thumbDivs[5].setAttribute('aria-label', '360 view');
      thumbDivs[5].removeAttribute('role');
      thumbDivs[5].removeAttribute('tabindex');
    }
  }, [imageList]);

  const onChange = (index: number) => {
    if (
      vehicleImages.imageList.length === index + 1 ||
      imageList?.length === index + 1
    ) {
      //last item found
      setLastItem(true);
      setFirstItem(false);
    } else if (index === 0) {
      //firstItem found
      setLastItem(false);
      setFirstItem(true);
    } else {
      setLastItem(false);
      setFirstItem(false);
    }
  };

  const customRenderThumb = (children: React.ReactChild[]) => {
    let childThumbs = children.map((item, index) => {
      return (
        <button type="button" className="image-gallery__thumb">
          <img
            src={
              React.isValidElement<{ url: string }>(item) ? item.props.url : ''
            }
            key={`img-thumb-${index}`}
            alt={thumbAccessibleNames[index]}
          />
        </button>
      );
    });
    const view360 = (
      <button
        type="button"
        className="image-gallery__thumb"
        aria-label="View exterior 360"
        onClick={() => onMainSelected(MainButtons.View360)}
      >
        <div key={uuid4()} className="view360">
          <View360Icon />
        </div>
      </button>
    );
    childThumbs.push(view360);
    return childThumbs;
  };

  return (
    <div
      data-cy="image-gallery-container__content-gallery"
      data-testid="image-gallery-container__content-gallery"
      className={cn(className)}
    >
      {vehicleImages.loading ? (
        <Loader />
      ) : (
        <>
          {' '}
          <div
            className={cn(
              'image-gallery-container__content-gallery-text',
              isMobile &&
                vehicleImages.imageList.length === 0 &&
                imageList?.length === 0 &&
                'image-gallery-container__content-gallery-text-no-image'
            )}
          >
            {vehicleImages.imageList.length === 0 && imageList?.length === 0 ? (
              <Typography size="rg" variant="body1">
                No image gallery view is available
              </Typography>
            ) : (
              galleryImageExplanation.split('\n').map((text) => {
                return (
                  <Typography key={uuid4()} size="rg" variant="body1">
                    {text}
                  </Typography>
                );
              })
            )}
          </div>
          <Carousel
            ariaLabel={`Examples of ${make} ${model} at different angles`}
            showStatus={false}
            onChange={onChange}
            showArrows={true}
            showThumbs={true}
            useKeyboardArrows={true}
            swipeable={true}
            emulateTouch={true}
            swipeScrollTolerance={5}
            width={currentWidth}
            showIndicators={false}
            thumbWidth={140}
            renderThumbs={customRenderThumb}
            renderArrowPrev={(onClickHandler, hasPrev, label) => (
              <button
                disabled={!hasPrev}
                type="button"
                onClick={onClickHandler}
                title={label}
                style={{
                  ...arrowStyles,
                  cursor: !hasPrev ? 'default' : 'pointer',
                  left: isMobile ? 10 : 40,
                }}
              >
                <PreviousChevron
                  {...(!hasPrev
                    ? {
                        stroke: 'var(--config-input-placeholder-colour)',
                        cursor: 'not-allowed',
                      }
                    : {})}
                />
              </button>
            )}
            renderArrowNext={(onClickHandler, hasNext, label) => (
              <button
                disabled={!hasNext}
                type="button"
                onClick={onClickHandler}
                title={label}
                style={{
                  ...arrowStyles,
                  cursor: !hasNext ? 'default' : 'pointer',
                  right: isMobile ? 10 : 40,
                }}
              >
                <NextChevron
                  {...(!hasNext
                    ? {
                        stroke: 'var(--config-input-placeholder-colour)',
                        cursor: 'not-allowed',
                      }
                    : {})}
                />
              </button>
            )}
          >
            {vehicleImages.imageList && vehicleImages.imageList.length > 0
              ? vehicleImages.imageList.map(
                  (image: VehicleImageInfo, index: number) => (
                    <GalleryImage
                      key={`vehicle-img-${index}`}
                      url={image.url}
                      altText={thumbAccessibleNames[index]}
                    />
                  )
                )
              : imageList?.map((image: VehicleImageInfo, index: number) => (
                  <GalleryImage
                    key={`vehicle-img-${index}`}
                    url={image.url}
                    altText={thumbAccessibleNames[index]}
                  />
                ))}
          </Carousel>
        </>
      )}
    </div>
  );
};

export default Gallery;
